import CardIcon from './CardIcon';
import { LocationCardProps } from './LocationCardWrapperV2';
import getStore from '../data/store';
import getLocationCardIconName from '../utils/getLocationCardIconName';
import { observer } from 'mobx-react';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { Shimmer, ShimmerElementType as ElemType } from 'office-ui-fabric-react/lib/Shimmer';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import { initializeLocationCardIfNeeded } from 'owa-location-card-v2/lib/actions/locationCardActions';
import { call, openDirections } from 'owa-location-card-v2/lib/components/LocationExpandedCard';
import getCompactCardActionStyle from 'owa-location-card-v2/lib/utils/getCompactCardActionStyle';
import getLocationCardBackgroundIconColor from 'owa-location-card-v2/lib/utils/getLocationCardIconBackgroundColor';
import getLocationCardIconColor from 'owa-location-card-v2/lib/utils/getLocationCardIconColor';
import strings from 'owa-strings/owa-calendar-strings';
import * as React from 'react';
import {
    hasCoordinates,
    getFormatterAddress,
    hasPhoneNumber,
    isCapacitySet,
    isWorkingHourSet,
    getBuildingString,
    getReservedRoomString,
    getWebsiteString,
    isLocationInCache,
} from 'owa-location-card-v2/lib/selectors/locationCardSelectors';

const ACTION_SOURCE = 'Action';

// The perfDataPoint will time out (stop tracking elapsed time inside the panel)
// after 5 minutes.  Note that if not set, the default time out is only 1 minute.
const MAX_TIME_IN_CARD: number = 300000;

let styles = require('./LocationCard.scss');

@observer
export default class LocationCompactCard extends React.Component<LocationCardProps, any> {
    private perfDatapoint: PerformanceDatapoint;
    private isNotInCacheOnMount: boolean;

    componentDidMount() {
        this.isNotInCacheOnMount = !isLocationInCache(this.props.locationId);
        this.perfDatapoint = new PerformanceDatapoint('LOCATIONCARD_ELAPSEDTIME', {
            timeout: MAX_TIME_IN_CARD,
        });

        initializeLocationCardIfNeeded(
            this.props.name,
            this.props.postalAddress,
            this.props.locationId,
            this.props.locationType,
            this.props.colorScheme
        );
    }

    /** Logging the location details on unmount */
    componentWillUnmount() {
        this.logData();
    }

    render() {
        const isDataLoaded = getStore().viewState.isDataLoaded;

        return (
            <div className={styles.compactCard}>
                <Shimmer
                    isDataLoaded={isDataLoaded}
                    width={276}
                    shimmerElements={[
                        { type: ElemType.circle, height: 72 },
                        { type: ElemType.gap, width: 16 },
                        { type: ElemType.line, height: 14, width: 188 },
                    ]}>
                    {this.renderCard()}
                </Shimmer>
            </div>
        );
    }

    private renderCard = (): JSX.Element => {
        const { place, viewState } = getStore();

        if (!place) {
            return null;
        }

        // personal places that are not enhanced location are considered as Unknown
        const cardType =
            place.Type == 'PersonalPlace' && !place.Coordinates ? 'Unknown' : place.Type;

        const showDirections = hasCoordinates() || !!getFormatterAddress();

        return (
            <>
                <div className={styles.cardHeader}>
                    <div className={styles.cardIcon}>
                        <CardIcon
                            iconName={getLocationCardIconName(cardType)}
                            backgroundColor={getLocationCardBackgroundIconColor(
                                cardType,
                                viewState.colorScheme
                            )}
                            iconColor={getLocationCardIconColor(cardType)}
                        />
                    </div>
                    <div className={styles.title}>{place.Name}</div>
                </div>
                <div className={styles.actions}>
                    {hasPhoneNumber() && (
                        <ActionButton
                            iconProps={{
                                iconName: ControlIcons.Phone,
                                styles: getCompactCardActionStyle(viewState.colorScheme),
                            }}
                            allowDisabledFocus={true}
                            onClick={call(ACTION_SOURCE)}>
                            {strings.call}
                        </ActionButton>
                    )}

                    {showDirections && (
                        <ActionButton
                            iconProps={{
                                iconName: ControlIcons.MapDirections,
                                styles: getCompactCardActionStyle(viewState.colorScheme),
                            }}
                            allowDisabledFocus={true}
                            onClick={openDirections(ACTION_SOURCE)}>
                            {strings.directions}
                        </ActionButton>
                    )}
                </div>
            </>
        );
    };

    /** Logs details about the card  when we are dismissing the cards*/
    private logData() {
        this.perfDatapoint.addCustomData([getStore().place.Type]);
        this.perfDatapoint.end();

        if (getStore().viewState.isLoading) {
            return;
        }

        // aggregate the summary: was the card in the cache, has address, has coordinates.
        const summary = this.hash(
            this.isNotInCacheOnMount,
            getFormatterAddress(),
            hasCoordinates()
        );

        // aggregate the room details
        const type = getStore().place.Type;
        const roomDetails =
            type == 'Room'
                ? this.hash(isCapacitySet(), getBuildingString(), getReservedRoomString())
                : 0;

        // aggregate the extended details
        const extendedDetails = this.hash(getWebsiteString(), hasPhoneNumber(), isWorkingHourSet());

        logUsage('LocationCardShown', [type, summary, roomDetails, extendedDetails]);

        // The callback is non-null when location card is in details form or peek. It could be null in txp, meeting request, etc.
        if (this.props.onLocationCardUnmounted) {
            this.props.onLocationCardUnmounted();
        }
    }

    private hash = (x, y, z): number => {
        let hash = 0;
        if (x) {
            hash += 100;
        }
        if (y) {
            hash += 10;
        }
        if (z) {
            hash += 1;
        }
        return hash;
    };
}
